import React, { useState } from 'react';
import { calculatePlantPositions } from '../algorithms/plantCalculations';
import theme from '../theme';

function PlantListModal({ show, onClose, plantGroups, cropAreas, onEdit, onDelete, onAddTodo, onExportCSV }) {
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  if (!show) return null;

  const toggleExpand = (groupId) => {
    setExpandedGroupId(expandedGroupId === groupId ? null : groupId);
  };

  const handleExportCSV = (plantGroup) => {
    const slotForPlantGroup = cropAreas.find(area => area.id === plantGroup.slotId);
    if (!slotForPlantGroup) {
      alert('Slot not found for this plant group');
      return;
    }

    const plants = calculatePlantPositions(
      slotForPlantGroup,
      plantGroup.rowSpacing,
      plantGroup.colSpacing,
      plantGroup.rowMargin ?? plantGroup.rowOffset ?? 0,
      plantGroup.colMargin ?? plantGroup.colOffset ?? 0,
      plantGroup.numRows,
      plantGroup.numCols
    );

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    let csvContent = `Plant Group: ${plantGroup.plantName}
Slot: ${plantGroup.slotName}
Generated: ${currentDate} ${currentTime}
Description: ${plantGroup.description || 'N/A'}

Configuration:
Row Spacing: ${plantGroup.rowSpacing} m
Column Spacing: ${plantGroup.colSpacing} m
Row Margin: ${plantGroup.rowMargin ?? plantGroup.rowOffset ?? 0} m
Column Margin: ${plantGroup.colMargin ?? plantGroup.colOffset ?? 0} m
Total Plants: ${plants.length}

Plant #,Row,Column,Latitude,Longitude
`;

    plants.forEach(plant => {
      csvContent += `${plant.index},${plant.row},${plant.col},${plant.position.lat.toFixed(6)},${plant.position.lng.toFixed(6)}\n`;
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${plantGroup.plantName.replace(/\s+/g, '_')}_${plantGroup.id}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Plant Groups</h2>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: theme.colors.mediumGrey,
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>

        {plantGroups.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            No plant groups created yet. Use the Plant tool to add your first plant group.
          </div>
        ) : (
          <div>
            {plantGroups.map(group => {
              const isExpanded = expandedGroupId === group.id;

              return (
                <div
                  key={group.id}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    marginBottom: '10px',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onClick={() => toggleExpand(group.id)}
                  >
                    <div>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>
                        {group.plantName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Slot: {group.slotName} | Plants: {group.totalPlants} ({group.actualRows} rows × {group.actualCols} cols)
                      </div>
                    </div>
                    <div style={{ fontSize: '20px' }}>
                      {isExpanded ? '▼' : '▶'}
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '15px', backgroundColor: 'white' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <strong>Description:</strong>
                        <div style={{ marginTop: '5px', color: '#666' }}>
                          {group.description || 'No description'}
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                        <div>
                          <strong>Row Spacing:</strong> {group.rowSpacing} m
                        </div>
                        <div>
                          <strong>Column Spacing:</strong> {group.colSpacing} m
                        </div>
                        <div>
                          <strong>Row Margin:</strong> {group.rowMargin ?? group.rowOffset ?? 0} m
                        </div>
                        <div>
                          <strong>Column Margin:</strong> {group.colMargin ?? group.colOffset ?? 0} m
                        </div>
                        <div>
                          <strong>Configured Rows:</strong> {group.numRows === 0 ? 'Auto' : group.numRows}
                        </div>
                        <div>
                          <strong>Configured Columns:</strong> {group.numCols === 0 ? 'Auto' : group.numCols}
                        </div>
                      </div>

                      <div style={{ marginBottom: '15px', fontSize: '12px', color: '#666' }}>
                        Created: {new Date(group.createdAt).toLocaleString()}
                        {group.updatedAt && group.updatedAt !== group.createdAt && (
                          <> | Updated: {new Date(group.updatedAt).toLocaleString()}</>
                        )}
                      </div>

                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(group);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: theme.colors.info,
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportCSV(group);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: theme.colors.success,
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Export CSV
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddTodo(group);
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: theme.colors.warning,
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Add Todo
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Are you sure you want to delete the plant group "${group.plantName}"?`)) {
                              onDelete(group.id);
                            }
                          }}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: theme.colors.danger,
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default PlantListModal;
